/* eslint-disable max-lines-per-function */
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { faCog } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AbsatzProduktivitaetForm,
  UmsatzAbsatzProduktivitaetForm,
  UmsatzProduktivitaetForm,
} from './Forms';
import { useTagebuchEinstellungen } from '@/domain/controlling/hooks/useTagebuchEinstellungen';
import { Anzeige } from '@/domain/controlling/entities/tagebuchAnzeige';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AbsatzVorgabe } from '@/domain/controlling/entities';
import { AbsatzVorgabe as GqlAbsatzVorgabe } from '@/gql/graphql';
import { useUpdateTagebuchEinstellungen } from '@/domain/controlling/hooks/useUpdateTagebuchEinstellungen';
import { maskitoParseNumber } from '@maskito/kit';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { EinstellungenUpdateFormSchema } from './types';
import { updateTagebuchEinstellungenSchema } from './schema';
import { formatNumber } from '../utils';
import { Label } from '@/components/ui/label';
import { useStandortContext } from '@/features/standortContext/StandortContext';

export function EinstellungenSheet() {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();

  const { umsatzplanungAnzeige: defaultAnzeige, tagebuchEinstellungen } =
    useTagebuchEinstellungen(standortId);
  const { updateUmsatzplanungAnzeige, updateTagebuchEinstellungen } =
    useUpdateTagebuchEinstellungen();

  const [umsatzplanungAnzeige, setUmsatzplanungAnzeige] =
    useState<Anzeige>(defaultAnzeige);
  const [isUpdating, setIsUpdating] = useState(false);

  const form = useForm<EinstellungenUpdateFormSchema>({
    values: {
      zielProduktivitaetUmsatz: formatNumber(
        tagebuchEinstellungen.umsatzProduktivitaet.zielProduktivitaet.toString(),
        false,
      ),
      zielProduktivitaetAbsatz: {
        value: formatNumber(
          tagebuchEinstellungen.absatzProduktivitaet.zielProduktivitaet.toString(),
          true,
        ),
        unit: tagebuchEinstellungen.absatzProduktivitaet.absatzVorgabe,
      },
    },
    resolver: zodResolver(updateTagebuchEinstellungenSchema),
  });

  function onSubmit(data: EinstellungenUpdateFormSchema) {
    setIsUpdating(true);
    updateTagebuchEinstellungen({
      tagebuchEinstellungenUpdateInput: {
        standortId,
        umsatzProduktivitaet: {
          zielProduktivitaet: maskitoParseNumber(
            data.zielProduktivitaetUmsatz ?? '',
            { decimalSeparator: ',' },
          ),
        },
        absatzProduktivitaet: {
          zielProduktivitaet: maskitoParseNumber(
            data.zielProduktivitaetAbsatz.value ?? '',
            { decimalSeparator: ',' },
          ),
          absatzVorgabe:
            data.zielProduktivitaetAbsatz.unit === AbsatzVorgabe.Mpt
              ? GqlAbsatzVorgabe.Mpt
              : GqlAbsatzVorgabe.Stueck,
        },
      },
    })
      .then(() => {
        updateUmsatzplanungAnzeige(umsatzplanungAnzeige);
        toast({
          title: t(
            'domain.controlling.umsatzplanung.einstellungen.update.success.title',
          ),
        });
        setOpen(false);
        setIsUpdating(false);
      })
      .catch((error) => {
        toast({
          description: t(
            'domain.controlling.umsatzplanung.einstellungen.update.error.description',
          ),
        });
        Sentry.captureException(error);
        setIsUpdating(false);
        setOpen(false);
      });
  }

  useEffect(() => {
    form.reset();
  }, [form, umsatzplanungAnzeige]);

  return (
    <Sheet
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        setUmsatzplanungAnzeige(defaultAnzeige);
      }}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon">
          <FontAwesomeIcon icon={faCog} />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[368px]">
        <SheetHeader>
          <SheetTitle>
            {t('domain.controlling.umsatzplanung.einstellungen.title')}
          </SheetTitle>
          <SheetDescription>
            {t('domain.controlling.umsatzplanung.einstellungen.subtitle')}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-4 py-6">
          <RadioGroup
            defaultValue={umsatzplanungAnzeige}
            onValueChange={(value: Anzeige) => setUmsatzplanungAnzeige(value)}>
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  className="data-[state=checked]:text-primary-blue-600 text-neutrals-300"
                  value="UMSATZ_PRODUKTIVITAET"
                  id="UMSATZ_PRODUKTIVITAET"
                />
                <Label
                  htmlFor="UMSATZ_PRODUKTIVITAET"
                  className="hover:cursor-pointer text-sm font-normal">
                  {t(
                    'domain.controlling.umsatzplanung.einstellungen.options.umsatzProduktivitaet.label',
                  )}
                </Label>
              </div>
              {umsatzplanungAnzeige === 'UMSATZ_PRODUKTIVITAET' && (
                <UmsatzProduktivitaetForm
                  key="umsatzProduktivitaetForm"
                  onSubmit={onSubmit}
                  form={form}
                />
              )}
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  className="data-[state=checked]:text-primary-blue-600 text-neutrals-300"
                  value="ABSATZ_PRODUKTIVITAET"
                  id="ABSATZ_PRODUKTIVITAET"
                />
                <Label
                  htmlFor="ABSATZ_PRODUKTIVITAET"
                  className="hover:cursor-pointer text-sm font-normal">
                  {t(
                    'domain.controlling.umsatzplanung.einstellungen.options.absatzProduktivitaet.label',
                  )}
                </Label>
              </div>
              {umsatzplanungAnzeige === 'ABSATZ_PRODUKTIVITAET' && (
                <AbsatzProduktivitaetForm
                  key="absatzProduktivitaetForm"
                  onSubmit={onSubmit}
                  form={form}
                />
              )}
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  className="data-[state=checked]:text-primary-blue-600 text-neutrals-300"
                  value="UMSATZ_ABSATZ_PRODUKTIVITAET"
                  id="UMSATZ_ABSATZ_PRODUKTIVITAET"
                />
                <Label
                  htmlFor="UMSATZ_ABSATZ_PRODUKTIVITAET"
                  className="hover:cursor-pointer text-sm font-normal">
                  {t(
                    'domain.controlling.umsatzplanung.einstellungen.options.umsatzUndAbsatzProduktivitaet.label',
                  )}
                </Label>
              </div>
              {umsatzplanungAnzeige === 'UMSATZ_ABSATZ_PRODUKTIVITAET' && (
                <UmsatzAbsatzProduktivitaetForm
                  key="umsatzAbsatzProduktivitaetForm"
                  onSubmit={onSubmit}
                  form={form}
                />
              )}
            </div>
          </RadioGroup>
        </div>
        <SheetFooter>
          <Button
            disabled={!form.formState.isValid || isUpdating}
            form="umsatzplanungEinstellungenForm">
            {!isUpdating
              ? t('domain.controlling.umsatzplanung.ziele.fertig')
              : t(
                  'domain.controlling.umsatzplanung.einstellungen.update.isSubmitting',
                )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
